import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { isAuthError } from '@/lib/errors.js';

/**
 * Watches both the TanStack Query cache and the mutation cache for an AuthError
 * caused by an expired WorkOS session (getAccessToken returns null after retries
 * and a force-refresh attempt, or the server itself rejects the token with a
 * 401/403). On detection, redirects silently to /login — the api-auth-provider
 * already attempted a silent token refresh before this fires, so reaching here
 * means the session is truly gone. Covering the mutation cache too matters: a
 * mutation's own `onError` (e.g. a "failed, please try again" toast) fires
 * independently of the query cache, so without this a mutation hit during an
 * expired session would show that toast and never redirect.
 *
 * Must be mounted inside both QueryClientProvider and TanStack Router's router context.
 */
export function useSessionExpiry() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Use refs so the cache subscribers never need to re-register on pathname changes.
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;

  // Prevent duplicate navigations if multiple queries/mutations fail simultaneously.
  const handledRef = useRef(false);

  useEffect(() => {
    const redirectToLogin = () => {
      if (handledRef.current) return;
      handledRef.current = true;
      const current = pathnameRef.current;
      sessionStorage.setItem('redirectAfterLogin', current);
      navigate({ to: '/login', search: { state: current } as any });
    };

    const unsubscribeQueries = queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated') return;
      if (event.action.type !== 'error') return;
      if (!isAuthError(event.action.error)) return;
      redirectToLogin();
    });

    const unsubscribeMutations = queryClient.getMutationCache().subscribe((event) => {
      if (event.type !== 'updated') return;
      if (event.mutation?.state.status !== 'error') return;
      if (!isAuthError(event.mutation.state.error)) return;
      redirectToLogin();
    });

    return () => {
      unsubscribeQueries();
      unsubscribeMutations();
    };
  }, [queryClient, navigate]);
}
